import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Printer } from "lucide-react";
import { PreviewContext } from "../../hooks/usePage";
import { api } from "../../lib/api";

export const LEGAL_SLUGS = ["privacy-policy", "refund-policy", "terms", "service-delivery"];

const withAnchors = (html) => {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  const toc = [...doc.querySelectorAll("h3")].map((h, i) => { h.id = `section-${i + 1}`; return { id: h.id, text: h.textContent }; });
  return { html: doc.body.innerHTML, toc };
};

const Toc = ({ toc }) => (
  <nav aria-label="Contents" className="legal-toc no-print lg:sticky lg:top-24 lg:self-start" data-testid="legal-toc">
    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-[#4A5568]">Contents</p>
    <ol className="space-y-2 border-l-2 border-gold pl-4 text-sm">
      {toc.map((t) => <li key={t.id}><a href={`#${t.id}`} className="text-navy transition-colors hover:text-burgundy" data-testid={`legal-toc-${t.id}`}>{t.text}</a></li>)}
    </ol>
  </nav>
);

export function LegalDocument({ slug }) {
  const preview = useContext(PreviewContext);
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  useEffect(() => {
    if (preview) return;
    setPage(null);
    api.get(`/public/legal/${slug}`).then((r) => setPage(r.data)).catch(() => setPage(false));
  }, [slug, preview]);
  const data = preview ? preview.content : page;
  const doc = data?.sections?.find((s) => s.key === "document")?.data;
  const { html, toc } = useMemo(() => withAnchors(doc?.body), [doc?.body]);
  useEffect(() => { if (data?.title) document.title = `${data.title} | CIArb Bahamas Branch`; }, [data?.title]);

  if (data === false) return (
    <div className="container-site py-24" data-testid="legal-not-found"><h1 className="text-4xl text-navy">Page not found</h1>
      <Link to="/" className="btn-primary mt-6">Back to home</Link></div>);
  if (!data) return <p className="container-site py-24" aria-busy="true">Loading…</p>;
  const onBodyClick = (e) => {
    const a = e.target.closest("a");
    const href = a?.getAttribute("href") || "";
    if (href.startsWith("/") && !href.startsWith("//")) { e.preventDefault(); navigate(href); window.scrollTo(0, 0); }
  };
  return (
    <article className="legal-page container-site py-12 lg:py-16" data-testid={`legal-page-${slug}`}>
      <div className="max-w-3xl border-b-2 border-gold pb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-burgundy">CIArb Bahamas Branch</p>
        {doc?.notice && <p role="note" className="mt-4 rounded border-2 border-dashed border-amber-500 bg-amber-50 px-4 py-2 font-bold text-amber-900" data-testid="legal-draft-notice">{doc.notice}</p>}
        <h1 className="mt-3 font-serif text-4xl text-navy sm:text-5xl" data-testid="legal-title">{data.title}</h1>
        {doc?.effective_date && <p className="mt-4 font-semibold text-navy" data-testid="legal-effective-date">Effective date: {doc.effective_date}</p>}
        <button type="button" onClick={() => window.print()} className="no-print mt-5 inline-flex items-center gap-2 rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-navy transition-colors hover:bg-slate-50" data-testid="legal-print-btn">
          <Printer size={16} aria-hidden="true" /> Print</button>
      </div>
      <div className="mt-10 grid gap-10 lg:grid-cols-[250px_minmax(0,1fr)]">
        {toc.length > 0 && <Toc toc={toc} />}
        <div className="legal-body max-w-3xl" onClick={onBodyClick} data-testid="legal-body" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </article>
  );
}

export const PrivacyPolicy = () => <LegalDocument slug="privacy-policy" />;
export const RefundPolicy = () => <LegalDocument slug="refund-policy" />;
export const Terms = () => <LegalDocument slug="terms" />;
export const ServiceDelivery = () => <LegalDocument slug="service-delivery" />;
